import { FC, useEffect, useState } from "react";
import Button from "../../form/Button";
import Input, { IInput, IInputOption, TInputEvent } from "../../form/Input";
import { TFormSize } from "../../form/TFormSize";
import Badge from "../Badge";
import DropDown from "../dropdown/DropDown";

export interface IFilterField {
  label?: string,
  Component?: FC<IInput>,
  value?: string,
  options?: IInputOption[],
  handle?: (e: TInputEvent) => void,
};
export type IFilter<T> = {
  [key in keyof T]: IFilterField;
};
export type IFilterState<T> = {
  [key in keyof T]: string;
};

const Filter : FC<{
  className?: string,
  fields: IFilter<any>,
  filterState: IFilterState<any>,
  setFilterState: (newState: IFilterState<any>) => void,
  filterCount?: number,
}> = ({
  className,
  fields,
  filterState,
  setFilterState,
  filterCount,
}) => {  
  const [localFilterState, setLocalFilterState] = useState<IFilterState<any>>(() => filterState);

  const clearApply = () => setFilterState(Object.keys(filterState).reduce((acc, fieldKey) => ({ ...acc, [fieldKey]: ''}), {}));   
  const handleApply = () => setFilterState(localFilterState);

  // -- Default getting applied filter counts
  const getAppliedFilterCount = () => Object.values(filterState)
    .filter((value) => !!value)
    .length;

  // -- When global state changes we need change the local state as well
  useEffect(() => {
    setLocalFilterState(() => filterState);
  }, [filterState]);

  return (
    <DropDown btn={
      <div className="flex gap-2 font-semibold text-sm items-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
          <g clipPath="url(#clip0_348_80)">
            <path fillRule="evenodd" clipRule="evenodd" d="M10.0625 1.75C9.89014 1.75 9.71947 1.78394 9.56023 1.8499C9.40099 1.91586 9.2563 2.01254 9.13442 2.13442C9.01255 2.2563 8.91587 2.40098 8.84991 2.56022C8.78395 2.71946 8.75 2.89014 8.75 3.0625C8.75 3.23486 8.78395 3.40553 8.84991 3.56477C8.91587 3.72401 9.01255 3.8687 9.13442 3.99057C9.2563 4.11245 9.40099 4.20913 9.56023 4.27509C9.71947 4.34105 9.89014 4.375 10.0625 4.375C10.4106 4.375 10.7444 4.23672 10.9906 3.99057C11.2367 3.74443 11.375 3.41059 11.375 3.0625C11.375 2.7144 11.2367 2.38056 10.9906 2.13442C10.7444 1.88828 10.4106 1.75 10.0625 1.75ZM7.91875 2.625C8.01916 2.13049 8.28744 1.68591 8.67814 1.36658C9.06883 1.04725 9.55791 0.872803 10.0625 0.872803C10.5671 0.872803 11.0562 1.04725 11.4469 1.36658C11.8376 1.68591 12.1058 2.13049 12.2063 2.625H14V3.5H12.2063C12.1058 3.9945 11.8376 4.43908 11.4469 4.75841C11.0562 5.07774 10.5671 5.25219 10.0625 5.25219C9.55791 5.25219 9.06883 5.07774 8.67814 4.75841C8.28744 4.43908 8.01916 3.9945 7.91875 3.5H0V2.625H7.91875ZM3.9375 6.125C3.5894 6.125 3.25556 6.26328 3.00942 6.50942C2.76328 6.75556 2.625 7.0894 2.625 7.4375C2.625 7.78559 2.76328 8.11943 3.00942 8.36557C3.25556 8.61172 3.5894 8.75 3.9375 8.75C4.2856 8.75 4.61944 8.61172 4.86558 8.36557C5.11172 8.11943 5.25 7.78559 5.25 7.4375C5.25 7.0894 5.11172 6.75556 4.86558 6.50942C4.61944 6.26328 4.2856 6.125 3.9375 6.125ZM1.79375 7C1.89416 6.5055 2.16244 6.06091 2.55314 5.74158C2.94383 5.42225 3.43291 5.2478 3.9375 5.2478C4.44209 5.2478 4.93117 5.42225 5.32186 5.74158C5.71256 6.06091 5.98084 6.5055 6.08125 7H14V7.875H6.08125C5.98084 8.3695 5.71256 8.81408 5.32186 9.13341C4.93117 9.45274 4.44209 9.62719 3.9375 9.62719C3.43291 9.62719 2.94383 9.45274 2.55314 9.13341C2.16244 8.81408 1.89416 8.3695 1.79375 7.875H0V7H1.79375ZM10.0625 10.5C9.7144 10.5 9.38056 10.6383 9.13442 10.8844C8.88828 11.1306 8.75 11.4644 8.75 11.8125C8.75 12.1606 8.88828 12.4944 9.13442 12.7406C9.38056 12.9867 9.7144 13.125 10.0625 13.125C10.4106 13.125 10.7444 12.9867 10.9906 12.7406C11.2367 12.4944 11.375 12.1606 11.375 11.8125C11.375 11.4644 11.2367 11.1306 10.9906 10.8844C10.7444 10.6383 10.4106 10.5 10.0625 10.5ZM7.91875 11.375C8.01916 10.8805 8.28744 10.4359 8.67814 10.1166C9.06883 9.79725 9.55791 9.6228 10.0625 9.6228C10.5671 9.6228 11.0562 9.79725 11.4469 10.1166C11.8376 10.4359 12.1058 10.8805 12.2063 11.375H14V12.25H12.2063C12.1058 12.7445 11.8376 13.1891 11.4469 13.5084C11.0562 13.8277 10.5671 14.0022 10.0625 14.0022C9.55791 14.0022 9.06883 13.8277 8.67814 13.5084C8.28744 13.1891 8.01916 12.7445 7.91875 12.25H0V11.375H7.91875Z" fill="#374151"/>
          </g>
          <defs>
            <clipPath id="clip0_348_80">
              <rect width="14" height="14" fill="white"/>
            </clipPath>
          </defs>
        </svg>
        <div className="pr-2 border-r-[1px] border-r-gray-200">
          Filter
        </div> 
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path fillRule="evenodd" clipRule="evenodd" d="M1.44024 4.06525C1.48088 4.02451 1.52916 3.99219 1.58231 3.97013C1.63546 3.94807 1.69244 3.93672 1.74999 3.93672C1.80754 3.93672 1.86452 3.94807 1.91767 3.97013C1.97082 3.99219 2.0191 4.02451 2.05974 4.06525L6.99999 9.00638L11.9402 4.06525C11.9809 4.02458 12.0292 3.99231 12.0824 3.9703C12.1355 3.94828 12.1925 3.93695 12.25 3.93695C12.3075 3.93695 12.3645 3.94828 12.4176 3.9703C12.4708 3.99231 12.5191 4.02458 12.5597 4.06525C12.6004 4.10593 12.6327 4.15422 12.6547 4.20737C12.6767 4.26052 12.688 4.31748 12.688 4.375C12.688 4.43253 12.6767 4.48949 12.6547 4.54264C12.6327 4.59579 12.6004 4.64408 12.5597 4.68475L7.30974 9.93475C7.2691 9.9755 7.22082 10.0078 7.16767 10.0299C7.11452 10.0519 7.05753 10.0633 6.99999 10.0633C6.94244 10.0633 6.88546 10.0519 6.83231 10.0299C6.77916 10.0078 6.73088 9.9755 6.69024 9.93475L1.44024 4.68475C1.3995 4.64411 1.36717 4.59584 1.34512 4.54268C1.32306 4.48953 1.31171 4.43255 1.31171 4.375C1.31171 4.31746 1.32306 4.26048 1.34512 4.20732C1.36717 4.15417 1.3995 4.10589 1.44024 4.06525Z" fill="#374151"/>
        </svg>
        {(() => {
          const appliedFilterCount = filterCount ?? getAppliedFilterCount();
          return appliedFilterCount
            ? <Badge>{appliedFilterCount}</Badge>
            : <></>
        })()}
      </div>
    }>
      <div className={`${className} flex flex-col gap-3`}>
        {!!Object.keys(fields)?.length && Object.entries(fields).map(([name, { options, label, value, Component, handle: handler }]) => {
          // -- Define common props (the same for all field types)
          const props = {
            size: "small" as TFormSize,
            name,
            label,
            value: localFilterState[name],
            handle: handler
              ? handler
              : (e: TInputEvent) => {                         
                setLocalFilterState({
                  ...localFilterState,
                  [name]: e?.target?.value || '',
                })
              }               
          };
                    
          return Component && <Component {...props} key={name} /> // If Component exists -> Component      
            || <Input {...props} key={name} /> // Default -> simple input             
        })}

        <div className="flex justify-center gap-4 mt-2">        
          <Button
            variant="outline"
            size="small"
            onClick={clearApply}
          >
            Clear
          </Button>  
          <Button
            size="small"
            onClick={handleApply}
          >
            Apply
          </Button> 
        </div>
      </div>
    </DropDown>
  );
};

export default Filter;